import { FC, ReactNode } from 'react';

import MuiSelect, { SelectProps as MuiSelectProps } from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Icon from 'components/Icon';

export type SelectOption<V extends string | number = string | number> = {
  value?: V;
  label: string;
  color?: string;
  options?: SelectOption<V>[];
  icon?: string;
};

type Props = {
  label?: ReactNode;
  labelId?: string;
  hasError?: boolean;
  errorMessage?: string;
  helperText?: ReactNode;
  options: SelectOption[];
} & MuiSelectProps<string>;

export type SelectBaseProps = Props;

const makeMenu = (opts: SelectOption[]) => {
  const result = [];

  let key = 0;
  for (let option of opts) {
    if (option.options) {
      result.push(<ListSubheader key={key++}>{option.label}</ListSubheader>);

      for (let second of option.options) {
        result.push(
          <MenuItem key={key++} value={second.value}>
            {second.icon && (
              <ListItemIcon>
                <Icon icon={second.icon} />
              </ListItemIcon>
            )}

            <ListItemText color={second.color}>{second.label}</ListItemText>
          </MenuItem>
        );
      }
    } else {
      result.push(
        <MenuItem key={key++} value={option.value}>
          {option.icon && (
            <ListItemIcon>
              <Icon icon={option.icon} />
            </ListItemIcon>
          )}

          <ListItemText color={option.color}>
            {!option.value ? <em>{option.label}</em> : option.label}
          </ListItemText>
        </MenuItem>
      );
    }
  }

  return result;
};

const SelectBase: FC<Props> = props => {
  const {
    label,
    labelId,
    hasError,
    options,
    helperText,
    errorMessage,
    value = '',
    fullWidth,
    sx,
    ...rest
  } = props;

  return (
    <FormControl fullWidth={fullWidth} sx={sx} variant="standard">
      {label && <InputLabel id={labelId}>{label}</InputLabel>}

      <MuiSelect {...rest} value={value} labelId={labelId} error={hasError}>
        {makeMenu(options)}
      </MuiSelect>

      {(hasError || helperText) && (
        <FormHelperText error={hasError}>
          {hasError ? errorMessage : helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default SelectBase;
